import React, { useState, useEffect, useContext } from 'react';
import { getRedirectResult } from "firebase/auth";
import { auth } from './firebase.js';
import { useDispatch } from 'react-redux';
import { login } from './userSlice';
import { useNavigate, Link } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { useTheme } from './ThemeContext';
import Spinner from './Spinner.js';
import AZNationalContactForm from './AZNationalContactForm.js';
import aznlHeroImage from './images/aznl_hero.png';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import queryString from "query-string";
import ReactPixel from "react-facebook-pixel";
import { useLocation } from "react-router-dom";
import { processAffiliateParams } from './affiliateHelpers';

function AZNationalLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentUser, loginWithEmail } = useContext(AuthContext);
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);

  const { carrierPortal } = theme;
  const location = useLocation();

  const handleContactClick = (e) => {
    e.preventDefault();
    
    if (location.pathname === '/') {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/#contact');
    }
    
    setIsNavOpen(false);
  };

  useEffect(() => {
      const params = queryString.parse(window.location.search);
      if (Object.keys(params).length > 0) {
        localStorage.setItem("marketingParams", JSON.stringify(params));
      }
      
      const searchParams = new URLSearchParams(window.location.search);
      const affiliateData = processAffiliateParams(searchParams);
      
      if (affiliateData) {
        console.log('Affiliate tracking activated:', affiliateData);
        ReactPixel.track('AffiliateVisit', {
          refCode: affiliateData.refCode,
          source: affiliateData.source
        });
      }
    }, []);

    useEffect(() => {
      const pageName = location.pathname.replace("/", "") || "Home";
  
      ReactPixel.pageView({
        page: pageName, 
        path: location.pathname,
        url: window.location.href
      });  

    }, [location]);

    useEffect(() => {
      if (location.hash === '#contact') {
        const timer = setTimeout(() => {
          const contactElement = document.getElementById('contact');
          if (contactElement) {
            contactElement.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);

        return () => clearTimeout(timer);
      }
    }, [location.hash]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const loginTimeout = setTimeout(() => {
      setIsLoading(false);
      setError('Login process is taking too long. Please try again.');
    }, 15000);

    try {
      await loginWithEmail(email, password);
      clearTimeout(loginTimeout);
    } catch (err) {
      console.error("Error Logging In:", err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      if (currentUser.isAffiliate === 'True' || currentUser.isAffiliate === true || currentUser.Roles === 'Affiliate') {
        navigate('/affiliate-dashboard');
      } else {
        navigate('/dashboard');
      }
    } else {
      getRedirectResult(auth)
        .then((result) => {
          if (result && result.user) {
            const { displayName, email, uid, photoURL } = result.user;
            dispatch(login({ displayName, email, uid, photoURL }));
          }
        })
        .catch((error) => {
          console.error("Error in getRedirectResult:", error);
          setError(error.message);
        });
    }
  }, [currentUser, dispatch, navigate]);

  return (
    <>
      {isLoading && <Spinner />}

      <div className="min-h-screen bg-white font-sans">
        {/* Header - Blue background with logo */}
        <header className="bg-white shadow-lg border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-4">
              {/* Logo */}
              <div className="flex items-center">
                <img 
                  src="/logos/az_national_logo.png" 
                  alt="Arizona National Livestock Show" 
                  className="h-16 w-auto"
                />
              </div>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center space-x-8">
                <div className="relative group">
                  <button className="flex items-center text-[#26314f] hover:text-[#c8872b] font-medium transition-colors">
                    HOME
                  </button>
                </div>
                <div className="relative group">
                  <button className="flex items-center text-[#26314f] hover:text-[#c8872b] font-medium transition-colors">
                    FUEL PROGRAM <FontAwesomeIcon icon={faChevronDown} className="ml-1 text-xs" />
                  </button>
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white shadow-lg border rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                    <Link to="/aznationallocations" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#26314f]">
                      Find Locations
                    </Link>
                    <Link to="/registration" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#26314f]">
                      Sign Up
                    </Link>
                  </div>
                </div>
                <a href="#contact" onClick={handleContactClick} className="text-[#26314f] hover:text-[#c8872b] font-medium transition-colors">
                  CONTACT
                </a>
                <button 
                  onClick={() => setShowLoginForm(!showLoginForm)}
                  className="bg-[#c8872b] hover:bg-[#a06d1f] text-white px-6 py-2 rounded-full font-medium transition-colors"
                >
                  MEMBER LOGIN
                </button>
              </nav>

              {/* Mobile menu button */}
              <div className="lg:hidden">
                <button
                  onClick={() => setIsNavOpen(!isNavOpen)}
                  className="text-[#26314f] hover:text-[#c8872b] transition-colors"
                >
                  <FontAwesomeIcon icon={faBars} className="text-xl" />
                </button>
              </div>
            </div>

            {/* Mobile Navigation */}
            {isNavOpen && (
              <div className="lg:hidden border-t border-white/20 py-4">
                <div className="space-y-3">
                  <Link to="/" className="block text-[#26314f] hover:text-[#c8872b] font-medium transition-colors">HOME</Link>
                  <Link to="/aznationallocations" className="block text-[#26314f] hover:text-[#c8872b] font-medium transition-colors">FUEL LOCATIONS</Link>
                  <a href="#contact" onClick={handleContactClick} className="block text-[#26314f] hover:text-[#c8872b] font-medium transition-colors">CONTACT</a>
                  <button 
                    onClick={() => setShowLoginForm(!showLoginForm)}
                    className="w-full text-left bg-[#c8872b] hover:bg-[#a06d1f] text-white px-4 py-2 rounded-full font-medium transition-colors"
                  >
                    MEMBER LOGIN
                  </button>
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Login Form Overlay */}
        {showLoginForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-[#26314f]">Member Login</h2>
                <button 
                  onClick={() => setShowLoginForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
              
              <p className="text-gray-600 text-sm mb-6">Access your Arizona National Fuel Program account</p>
              
              {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8872b] focus:border-[#c8872b] outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8872b] focus:border-[#c8872b] outline-none"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#c8872b] hover:bg-[#a06d1f] text-white font-bold py-3 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </button>
              </form>

              <div className="mt-6 text-center space-y-2 text-sm">
                <div>
                  <Link to="/resetpassword" className="text-[#26314f] hover:underline">
                    Forgot your password?
                  </Link>
                </div>
                <div className="border-t pt-4">
                  <Link to="/registration" className="text-[#c8872b] hover:underline font-medium">
                    New to our program? Sign up here
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Hero Section - Clean WordPress style */}
        <section className="py-16 lg:py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold text-[#26314f] mb-6">
                  Arizona National Fuel Program
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Save money on fuel and support our scholarship programs. Every gallon you purchase helps fund scholarships for young people pursuing post-secondary education, including colleges, universities, and trade schools.<br />
                  <strong>Fill your tank. Fuel a dream.</strong>
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-[#c8872b]">
                    <div className="text-2xl font-bold text-[#c8872b] mb-2">Up to $0.15/gal</div>
                    <div className="text-sm font-semibold text-gray-800 mb-1">Diesel Savings</div>
                    <p className="text-gray-600 text-sm">Instant discount for supporters</p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-[#26314f]">
                    <div className="text-2xl font-bold text-[#26314f] mb-2">$0.05/gal</div>
                    <div className="text-sm font-semibold text-gray-800 mb-1">Goes to Scholarship Programs</div>
                    <p className="text-gray-600 text-sm">Supporting the next generation</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/registration"
                    className="bg-[#c8872b] hover:bg-[#a06d1f] text-white px-8 py-3 rounded-full font-semibold text-center transition-colors"
                  >
                    Get Started Today
                  </Link>
                  <Link
                    to="/aznationallocations"
                    className="border-2 border-[#26314f] text-[#26314f] hover:bg-[#26314f] hover:text-white px-8 py-3 rounded-full font-semibold text-center transition-colors"
                  >
                    Find Locations
                  </Link>
                </div>
              </div>

              {/* Hero Image */}
              <div className="order-first lg:order-last">
                <div className="rounded-lg overflow-hidden shadow-lg">
                  <img 
                    src={aznlHeroImage}
                    alt="Arizona National Livestock Show" 
                    className="w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#26314f] mb-4">
                How Our Fuel Program Works
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Simple, straightforward fuel savings that support the livestock community
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-[#c8872b] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">1</span>
                </div>
                <h3 className="text-xl font-semibold text-[#26314f] mb-3">Sign Up Free</h3>
                <p className="text-gray-600">
                  No credit checks, no fees. Just provide your information and you're ready to start saving.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-[#c8872b] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">2</span>
                </div>
                <h3 className="text-xl font-semibold text-[#26314f] mb-3">Get Your Cards</h3>
                <p className="text-gray-600">
                  Order fuel cards for your family, ranch hands, or different operations. Set spending limits as needed.
                </p>
              </div>

              <div className="text-center">
                <div className="bg-[#c8872b] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white text-2xl font-bold">3</span>
                </div>
                <h3 className="text-xl font-semibold text-[#26314f] mb-3">Start Saving</h3>
                <p className="text-gray-600">
                  Use your cards at 4,500+ locations nationwide. Save money and support youth programs automatically.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Partner Locations */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#26314f] mb-4">
                Nationwide Partner Network
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Find fuel savings at major travel centers and truck stops across the country
              </p>
              <Link
                to="/aznationallocations"
                className="inline-block bg-[#26314f] hover:bg-[#1a2238] text-white px-8 py-3 rounded-full font-semibold transition-colors"
              >
                View All Locations
              </Link>
            </div>

            {/* Partner logos grid */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4 md:gap-6 items-center">
              <div className="flex justify-center">
                <img src="/logos/ta-tri-logo-primary.svg" alt="TA TravelCenters" className="h-10" />
              </div>
              <div className="flex justify-center">
                <img src="/logos/speedwayLogo.svg" alt="Speedway" className="h-10" />
              </div>
              <div className="flex justify-center">
                <img src="/logos/racetracLogo.png" alt="RaceTrac" className="h-10" />
              </div>
              <div className="flex justify-center">
                <img src="/logos/maverikLogo.svg" alt="Maverik" className="h-10" />
              </div>
              <div className="flex justify-center">
                <img src="/logos/sappBrosLogo.png" alt="Sapp Bros" className="h-10" />
              </div>
              <div className="flex justify-center">
                <img src="/logos/caseysLogo.svg" alt="Casey's" className="h-10" />
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16 bg-[#26314f] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Start Saving and Supporting Scholarship Programs?
            </h2>
            <p className="text-xl mb-8 text-gray-300">
              Save money on fuel while supporting our scholarship programs. Our programs provide $60,000 annually to students pursuing bachelor's degrees and trade schools, with $1.4 million awarded since inception.
            </p>
            <Link
              to="/registration"
              className="inline-block bg-[#c8872b] hover:bg-[#a06d1f] text-white px-12 py-4 rounded-full text-lg font-semibold transition-colors"
            >
              Sign Up Now - It's Free
            </Link>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#26314f] mb-4">
                Questions About Our Fuel Program?
              </h2>
              <p className="text-lg text-gray-600">
                We're here to help you get started or answer any questions you might have.
              </p>
            </div>
            <AZNationalContactForm />
          </div>
        </section>

        {/* Footer - Simple WordPress style */}
        <footer className="bg-gray-100 py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="text-gray-600 text-sm mb-4 md:mb-0">
                © {new Date().getFullYear()} Arizona National Livestock Show Fuel Program. All rights reserved.
              </div>
              <div className="flex space-x-6 text-sm">
                <Link to="/faq" className="text-gray-600 hover:text-[#26314f]">FAQ</Link>
                <Link to="/resetpassword" className="text-gray-600 hover:text-[#26314f]">Reset Password</Link>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default AZNationalLogin;